<script setup>
import { reactive, watch, computed } from 'vue'
import { useRoute } from 'vue-router'

import BaseToggle from '@dogfy/components/base/BaseToggle.vue'
import BaseButton from '@dogfy/components/base/BaseButton.vue'
import BaseIcon from '@dogfy/components/base/BaseIcon.vue'

import { useBreakpoints } from '@dogfy/composables/useBreakpoints'
import config from '@dogfy/config'

const cookieModel = defineModel({ type: Object })

const props = defineProps({
  image: {
    type: String,
    default: '',
  },
  privacyUrl: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['save'])

const route = useRoute()
const { isMobileOrTablet } = useBreakpoints()

const state = reactive({
  isModalInfoVisible: false,
  isModalSettingsVisible: false,
  selectedCookieIndex: 0,
})

const permissions = reactive([
  { consentTypes: config.cookies.consent.types.needed, key: 'needed', enabled: true, mandatory: true },
  { consentTypes: config.cookies.consent.types.security, key: 'security', enabled: true, mandatory: true },
  { consentTypes: config.cookies.consent.types.marketing, key: 'marketing', enabled: true, mandatory: false },
  { consentTypes: config.cookies.consent.types.statistics, key: 'statistics', enabled: true, mandatory: false },
])

const canBeDisplayed = computed(() => !cookieModel.value && !config.cookies.excludedPaths.includes(route.path))

const isOpen = computed(() => state.isModalInfoVisible || state.isModalSettingsVisible)

function showModalSettings () {
  state.isModalInfoVisible = false
  state.isModalSettingsVisible = true
}

function toggleDetails (index) {
  const selectedCookie = permissions[index]
  selectedCookie.isDescriptionVisible = !selectedCookie.isDescriptionVisible
}

watch(() => route.path, () => {
  if (canBeDisplayed.value) {
    state.isModalInfoVisible = true
  } else {
    state.isModalSettingsVisible = false
    state.isModalInfoVisible = false
  }
}, { immediate: true })

if (typeof window !== 'undefined') {
  watch(isOpen, value => {
    // Do not scroll body content when modal is visible.
    document.body.style.overflow = value ? 'hidden' : 'visible'
  }, { immediate: true })
}

function saveSettings () {
  const settingsCookie = {}
  for (const permission of permissions) {
    for (const consentType of permission.consentTypes) {
      settingsCookie[consentType] = permission.enabled
    }
  }

  cookieModel.value = settingsCookie

  state.isModalSettingsVisible = false
  state.isModalInfoVisible = false

  emit('save')
}
</script>

<template>
  <Transition name="fade">
    <div v-if="isOpen" class="cookies-popup" role="dialog">
      <div class="cookies-popup__content">
        <div class="cookies-popup__header">
          <div class="cookies-popup__image-container">
            <img class="cookies-popup__image" :src="props.image">
          </div>
          <p class="cookies-popup__title mobile-only">
            <template v-if="state.isModalInfoVisible">
              {{ $t('cookiesConsent.label.infoBanner') }}
            </template>
            <template v-else>
              {{ $t('cookiesConsent.label.settingsBanner') }}
            </template>
          </p>
        </div>
        <div class="cookies-popup__body">
          <div class="cookies-popup__info">
            <p class="cookies-popup__title desktop-only">
              <template v-if="state.isModalInfoVisible">
                {{ $t('cookiesConsent.label.infoBanner') }}
              </template>
              <template v-else>
                {{ $t('cookiesConsent.label.settingsBanner') }}
              </template>
            </p>
            <p class="cookies-popup__description">
              <template v-if="state.isModalInfoVisible">
                {{ `${$t('cookiesConsent.message.infoBanner')} ${$t('cookiesConsent.message.cookiesTypeInfo')}` }}
              </template>
              <template v-else>
                {{ $t('cookiesConsent.message.cookiesTypeInfo') }}
              </template>
            </p>
          </div>
          <div v-if="state.isModalSettingsVisible" class="cookies-popup__selector-menu">
            <div v-for="(cookie, index) in permissions" :key="index" class="cookies-popup__accordion">
              <div class="cookies-popup__cookie-details">
                <p class="cookies-popup__cookie-title" @click="toggleDetails(index)">
                  <BaseIcon
                    class="cookies-popup__icon"
                    icon="chevron-down"
                    :class="{ 'cookies-popup__icon--rotate': cookie.isDescriptionVisible }"
                  />
                  {{ $t(`cookiesConsent.label.${cookie.key}`) }}
                </p>
                <BaseToggle
                  v-model="cookie.enabled"
                  class="cookies-popup__toggle"
                  :label="!isMobileOrTablet ? (cookie.enabled ? $t('cookiesConsent.label.enabled') : $t('cookiesConsent.label.disabled')) : null"
                  :is-disabled="cookie.mandatory"
                />
              </div>
              <Transition name="dropdown">
                <p v-if="cookie.isDescriptionVisible" class="cookies-popup__message">
                  {{ $t(`cookiesConsent.message.${cookie.key}`) }}
                </p>
              </transition>
            </div>
          </div>
          <div class="cookies-popup__actions" :class="{ 'cookies-popup__actions--row': state.isModalSettingsVisible && !isMobileOrTablet }">
            <BaseButton
              v-if="state.isModalInfoVisible"
              type="link-neutral small"
              class="cookies-popup__link-button"
              @click="showModalSettings"
            >
              {{ $t('cookiesConsent.cta.customize') }}
            </BaseButton>
            <BaseButton
              v-else
              tag="a"
              class="cookies-popup__link-button cookies-popup__link-button--info"
              type="link-color small"
              :href="privacyUrl"
            >
              {{ $t('cookiesConsent.label.moreInfo') }}
            </BaseButton>
            <button
              id="btnCookiesConsent"
              class="button button--fullwidth"
              :class="{ 'button--small': !isMobileOrTablet }"
              @click="saveSettings"
            >
              <template v-if="state.isModalInfoVisible">
                {{ $t('common.cta.accept') }}
              </template>
              <template v-else>
                {{ $t('cookiesConsent.cta.saveMySettings') }}
              </template>
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<style lang="scss">
.cookies-popup {
  position: fixed;
  z-index: z-number(modal);

  background-color: rgba($color: $color-neutral-darkest, $alpha: $opacity-level-medium);

  inset: 0;

  &__content {
    position: absolute;
    bottom: 0;

    display: flex;

    flex-direction: column;
    gap: $spacer*1.25;

    padding: $spacer*1.5 $spacer;

    background-color: $color-neutral-white;

    border-top-left-radius: $border-radius-lg;
    border-top-right-radius: $border-radius-lg;

    @include breakpoint(md) {
      left: 50%;

      transform: translateX(-50%);

      width: 720px;
    }

    @include breakpoint(lg) {
      flex-direction: row;
      gap: $spacer*1.5;

      padding: $spacer*1.5;
    }
  }

  &__header {
    display: flex;

    gap: $spacer;
  }

  &__image-container {
    position: relative;

    width: 80px;
    min-width: 80px;
    height: 48px;

    @include breakpoint(lg) {
      min-width: 190px;
      height: 200px;
    }
  }

  &__image {
    position: absolute;
    bottom: 0;

    width: 100%;

    @include breakpoint(lg) {
      bottom: 40px;
    }
  }

  &__body {
    display: flex;

    flex-direction: column;
    gap: $spacer;
  }

  &__info {
    display: flex;

    flex-direction: column;
    gap: $spacer-half;

    font-size: ms(0, true);
    line-height: $font-lineheight-large;
    font-weight: $font-weight-regular;
    color: $color-neutral-darkest;
  }

  &__title {
    font-size: ms(1, true);
    line-height: $font-lineheight-long;
    font-weight: $font-weight-semibold;
    color: $color-neutral-darkest;
  }

  &__description {
    font-size: ms(0, true);
    line-height: $font-lineheight-large;
    font-weight: $font-weight-regular;
    color: $color-neutral-darkest;
  }

  &__actions {
    display: flex;

    flex-direction: column;
    gap: $spacer-half;
    align-items: center;

    padding-top: $spacer-quarter;

    @include breakpoint(lg) {
      justify-content: space-between;

      padding-top: unset;
    }

    &--row {
      flex-direction: row;
    }
  }

  &__link-button {
    font-size: ms(0, true);
    line-height: $font-lineheight-large;
    font-weight: $font-weight-regular;
    color: $color-neutral-darkest;

    white-space: pre;

    &--info {
      color: $color-secondary-medium;

      @include breakpoint(lg) {
        text-wrap: nowrap;
      }
    }
  }

  &__selector-menu {
    display: flex;

    flex-direction: column;
    gap: $spacer*1.5;
  }

  &__accordion {
    display: flex;

    flex-direction: column;
    gap: $spacer-half;
    align-items: flex-start;
    justify-content: space-between;
  }

  &__cookie-details {
    display: flex;

    gap: $spacer-three-quarters;
    justify-content: space-between;

    width: 100%;
  }

  &__cookie-title {
    display: flex;

    gap: $spacer-half;
    align-items: center;

    cursor: pointer;

    font-size: ms(0, true);
    line-height: $font-lineheight-large;
    font-weight: $font-weight-regular;
    color: $color-neutral-darkest;
  }

  &__message {
    font-size: ms(-1, true);
    line-height: $font-lineheight-long;
    color: $color-neutral-darkest;
  }

  &__icon {
    transition: transform $transition-duration-fastest linear;

    &--rotate {
      transform: rotate(180deg);
    }
  }

  &__toggle {
    line-height: $font-lineheight-large;
    font-weight: $font-weight-regular;
  }
}
</style>
